import React, { useState } from 'react';
import { Button, Input } from 'lift-ui';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import validator from 'validator';

import apiClient from '@lift-admin/utils/apiClient';
import { Toastify } from 'lift-ui';
import { useAuthContext } from '@lift-admin/utils/provider';
import { ActionLogTrack } from '@lift-admin/utils/actionLogTrack';
import { LoginAnimatedBackground } from '../../ui/LoginAnimatedBackground';

interface loginProps {
  changeUserAction: (agr: string) => void;
}

const Login = ({ changeUserAction }: loginProps) => {
  const navigate = useNavigate();

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [showLoading, setShowLoading] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { loginUserWithPasswordEmail } = useAuthContext();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (passwordError) setPasswordError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Form validation
    if (!email) {
      setEmailError('Email is required');
      setPasswordError('');
      return;
    } else if (!validator.isEmail(email)) {
      setEmailError('Please enter a valid email');
      setPasswordError('');
      return;
    } else if (!password) {
      setEmailError('');
      setPasswordError('Password is required');
      return;
    } else {
      setEmailError('');
      setPasswordError('');
    }

    setShowLoading(true);

    try {
      const userCredentails = await loginUserWithPasswordEmail(email, password);

      if (!userCredentails.user.emailVerified) {
        Toastify('error', 'Email not verified!!. Please verify your email');
        setShowLoading(false);
        return;
      }

      const timeLine = localStorage.getItem('notification_time_line');
      localStorage.clear();
      const userResult = await apiClient.get(
        `/user/v1/user/admin/email/${email}`
      );

      const userResponse = userResult.data.response;

      const userData = {
        id: userResponse.id,
        name: userResponse.name,
        email,
        user_hash: userResponse.user_hash,
        brand: userResponse.brand,
        brand_hash: userResponse.brand_hash,
        image: userResponse.image,
        track: userResponse.track_usage,
        team: userResponse.team,
        admin: userResponse.admin_verified,
        weights: userResponse.weights,
        fintech_brands: userResponse.fintech_brands,
        brand_pub_mapping: userResponse.brand_pub_mapping,
        brand_goals: userResponse.brand_goals,
        brand_images: userResponse.brand_images,
        // Omit notifications — can be ~MB+ and is not read from storage; breaks localStorage quota
        taxonomyNotifications: userResponse.taxonomyNotifications,
        mobile: userResponse.mobile,
        meeting_link: userResponse.meeting_link,
        position: userResponse.position,
        is_admin: userResponse.is_admin,
        verified: userResponse?.verified,
      };
      sessionStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('super_admin', JSON.stringify(userData));

      let actionLog = new ActionLogTrack(userData);
      const desc = {
        message: `User with email ${email} logged in`,
        status: userResult.status,
        method: userResult.config.method,
        url: userResult.config.url,
      };

      await actionLog.actionLogTrack(JSON.stringify(desc), false);

      navigate('/home', {
        state: {
          email: email,
          is_admin: userResponse.is_admin,
        },
      });

      Toastify('success', 'Login Successfully');
    } catch (err: any) {
      console.log(err, 'err');
      if (err.response && err.response.data && err.response.data.message) {
        Toastify('error', err.response.data.message);
      } else {
        Toastify('error', err.message);
      }
      setShowLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex min-w-full">
      {/* Left side - Form */}
      <div className="w-full md:w-1/2 bg-white flex flex-col justify-center items-center p-0 min-h-full relative">
        {/* Logo at top-left */}
        <button
          type="button"
          className="absolute top-6 left-8"
          onClick={() => navigate('/')}
          aria-label="Go to home"
        >
          <img
            src="https://storage.googleapis.com/ltp-static-data/lift_assets/platform-logo.svg"
            alt="LIFT"
            className="h-6 w-auto"
          />
        </button>

        {/* Centered form */}
        <div className="max-w-md w-full px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">WELCOME BACK!</h1>
            <p className="text-gray-600">Log in to your LIFT Admin</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="jdoe@newengen.com"
                disabled={showLoading}
                className={emailError ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {emailError && <p className="text-red-600 text-sm mt-1">{emailError}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••••"
                  className={`pr-10 w-full ${passwordError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  value={password}
                  onChange={handlePasswordChange}
                  disabled={showLoading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center z-10"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={showLoading}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordError && <p className="text-red-600 text-sm mt-1">{passwordError}</p>}
            </div>

            {/* Forgot Password Link */}
            <button
              type="button"
              className="text-sm hover:underline"
              onClick={() => changeUserAction('forgot-pwd')}
            >
              Forgot password?
            </button>

            {/* Submit Button */}
            <Button type="submit" variant="default" className="w-full" disabled={showLoading}>
              {showLoading ? 'Logging in...' : 'Log In'}
            </Button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-6 text-center hidden">
            <span className="text-gray-600">Don't have an account? </span>
            <button
              type="button"
              onClick={() => changeUserAction('reg')}
              className="text-gray-900 font-medium hover:underline"
            >
              Sign Up
            </button>
          </div>
        </div>
      </div>

      {/* Right side - Animated Background */}
      <LoginAnimatedBackground />
    </div>
  );
};

export default Login;
